<template>
    <div class="integration-form">
         <b-card>
            <h2 class="">{{ $t('integration.edit_integration') }}</h2>
            <b-form @submit.prevent="onSubmit" enctype="multipart/form-data">
                <!-- for selecting service  -->
                <div class="mb-3 text-left">
                    <label class="mb-1" for="integration_service_id">
                        {{ $t('integration.select_your_integration_service') }}
                    </label>
                        <treeselect
                            :placeholder="$t('integration.select_your_integration_service')"
                            v-model="formFields.integration_service_id"
                            class="form-input"
                            :class="formErrors.has('integration_service_id') ? 'error-input' : ''"
                            :options="serviceOptions"
                            @input="handleServiceChange"
                        ></treeselect>

                    <b-form-invalid-feedback :state="!formErrors.has('integration_service_id')">
                        {{ formErrors.first('integration_service_id') }}
                    </b-form-invalid-feedback>
                </div>
                <div class="mb-3 text-left">
                    <label for="name" class="mb-1">{{
                        $t('integration.organization_name') }}</label>
                    <b-form-input id="name" type="text" v-model="formFields.name"
                        :placeholder="$t('integration.organization_name')"></b-form-input>

                    <b-form-invalid-feedback :state="!formErrors.has('name')">
                        {{ formErrors.first('name') }}
                    </b-form-invalid-feedback> <!-- Show error message -->
                </div>

                <div v-for="(label, key) in dynamicFields" :key="key" class="mb-3 text-left">
                    <label :for="label" class="mb-1">
                        {{ $t(`integration.${label}`) }}
                    </label>
                    <b-input-group
                        v-if="label !== 'google_service_account_credentials_json' && label !== 'product_service'">
                        <b-form-input :id="String(label)" v-model="formFields[label]"
                            :type="passwordVisibility[label] ? 'text' : 'password'"
                            :placeholder="$t(`integration.${label}`)"></b-form-input>
                        <b-input-group-append>
                            <b-button @click="toggleVisibility(label)" variant="light">
                                <b-icon :icon="passwordVisibility[label] ? 'eye-slash' : 'eye'"></b-icon>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>


                     <treeselect
                            v-else-if="label === 'product_service'"
                            :placeholder="$t(`integration.${label}`, { defaultValue: label })"
                            v-model="formFields[label]"
                            class="form-input"
                            :options="productsServicesOptions"
                        ></treeselect>
                    <b-form-file v-else :id="String(label)"
                        v-model="formFields.google_service_account_credentials_json"
                        :placeholder="$t(`integration.${label}`)"></b-form-file>


                    <b-form-invalid-feedback :state="!formErrors.has(label)">
                        {{ formErrors.first(label) }}
                    </b-form-invalid-feedback>
                </div>


                <!-- status  -->
                <div class="mb-3 text-left">
                    <label for="status" class="mb-1" :class="statusState">
                        {{ $t('integration.select_status') }}
                    </label>
                    <treeselect
                            :placeholder="$t('integration.select_status')"
                            v-model="formFields.is_active"
                            class="form-input"
                            :class="formErrors.has('is_active') ? 'error-input' : ''"
                            :options="statusOptions"
                            @input="removeError('is_active')"
                        ></treeselect>

                    <b-form-invalid-feedback :state="!formErrors.has('is_active')">
                        {{ formErrors.first('is_active') }}
                    </b-form-invalid-feedback>
                </div>
                <div class="mb-3 text-left">
                    <label for="limit" class="mb-1" :class="statusLimit">
                        {{ $t('integration.select_limit') }}
                    </label>
                    <b-form-datepicker id="date_picker" v-model="formFields.selected_date" reset-button
                        today-button locale="en" class="w-100">
                    </b-form-datepicker>
                    <b-form-invalid-feedback :state="!formErrors.has('limit')">
                        {{ formErrors.first('limit') }}
                    </b-form-invalid-feedback>
                </div>

                <div class="d-flex gap-2 mt-4">
                <b-button
                    type="submit"
                    variant="primary"
                    :disabled="isSubmitting"
                >
                    <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
                    {{$t('action.submit')}}
                </b-button>
                <b-button variant="secondary" @click="cancel">{{$t('action.cancel')}}</b-button>
            </div>
            </b-form>
        </b-card>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { reactive } from 'vue';
import { request } from "@/Util/Request";
import Error from "@/Util/Error";
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';


const FORM_STATE = {
    integration_service_id: null,
    name: '',
    services: 'Select a service',
    clientId: '',
    clientSecret: '',
    tenantId: '',
    microsoft_client_id: '',
    microsoft_client_secret: '',
    microsoft_tenant_id: '',
    grant_type: '',
    username: '',
    password: '',
    instance_url: '',
    domain_name: '',
    domain_admin_email: '',
    code: '',
    redirect_uri: '',
    google_service_account_credentials_json: null,
    is_active: null,
    selected_date: null,
    api_access_token: ''
};

export default {
    name: "IntegrationUpdate",
    data() {
        return {
            existing_google_credentials_url: '',
            formFields: { ...FORM_STATE },
            formErrors: new Error({}),
            serviceOptions: [],
            statusOptions: [
                { label: this.$t('integration.active'), id: '1' },
                { label: this.$t('integration.inactive'), id: '0' },
            ],
            dynamicFields: {},
            selectedService: null,
            allProductServicesOptions: {
                salesforce: [
                    { label: this.$t('integration.crm'), id: 'CRM' },
                    { label: this.$t('integration.analytics'), id: 'Analytics' },
                    { label: this.$t('integration.net_zero_cloud'), id: 'Net Zero Cloud' },
                    { label: this.$t('integration.financial_services_cloud'), id: 'Financial Services Cloud' },
                    { label: this.$t('integration.communications_cloud'), id: 'Communications Cloud' },
                    { label: this.$t('integration.education_cloud'), id: 'Education Cloud' },
                ],
                zoho: [
                    { label: this.$t('integration.crm'), id: 'CRM' },
                ],
            },
            defaultDate: new Date(),
            passwordVisibility: {},
            originalForm: {},
            isFormDirty: false,
            nextRoute: '',
            isSubmitting: false
        };
    },
    components: {Treeselect },
    computed: {
        ...mapGetters(['loader']),
        productsServicesOptions() {
            if(this.selectedService) {
                const selectedServiceName = this.selectedService.label.toLowerCase();
                if (selectedServiceName.includes('salesforce')) {
                    return this.allProductServicesOptions.salesforce;
                } else if (selectedServiceName.includes('zoho')) {
                    return this.allProductServicesOptions.zoho;
                }
            }
            
            return [];
        },
        nameState() {
            return this.formFields.name === '' ? '' : this.formErrors.$errors.name ? 'is-invalid' : 'is-valid';
        },
        statusState() {
            return this.formFields.status === null ? '' : this.formErrors.status ? 'is-invalid' : 'is-valid'; // Add computed for status
        },
        statusLimit() {
            return this.formFields.limit === null ? '' : this.formErrors.limit ? 'is-invalid' : 'is-valid'; // Add computed for status
        }
    },

    methods: {
        async removeError(key) {
            if (typeof key === 'object') {
                for (let i = 0; i < key.length; i++) {
                    this.formErrors.remove(key[i]);
                }
            } else {
                this.formErrors.remove(key);
            }
        },
        cancel() {
            this.$router.back();
        },
        goBackToDestination() {
            if(this.nextRoute) {
                this.$router.push(this.nextRoute);
            }
        },
        handleNavigation(crumb) {
            if (this.isFormDirty) {
                this.$refs['inputChangeConfirmationModal'].handleToggleModal();
                this.nextRoute = crumb.path; // Store next route
            } else {
                this.$router.push(crumb.path); // Navigate if no unsaved changes
            }
        },
        markAsDirty() {
            this.isFormDirty = true;
        },
        checkForChanges() {
            this.isFormDirty = JSON.stringify(this.formFields) !== JSON.stringify(this.originalForm);
        },
        resetForm() {
            this.formFields = reactive({
                name: '',
                selected_date: null,
                status: { value: '1', label: this.$t('integration.Active') },
            });
        },
        initializeVisibility() {
            Object.keys(this.formFields).forEach((key) => {
                if (!(key in this.passwordVisibility)) {
                    this.$set(this.passwordVisibility, key, false);
                }
            });
        },
        toggleVisibility(field) {
            this.passwordVisibility[field] = !this.passwordVisibility[field];
        },
        async fetchServices() {
            try {
                const response = await request({ method: 'get', url: `/admin/services` });

                this.serviceOptions = response.data.map(service => ({
                    id: service.id,
                    label: service.name,
                    fields: service.fields || {}
                }));

            } catch (error) {
                console.error('Error fetching services:', error);
            }
        },



        handleServiceChange(serviceId) {
            const selectedService = this.serviceOptions.find(service => service.id === serviceId);
            this.dynamicFields = selectedService?.fields || {};
            this.selectedService = selectedService;
        },


        // Validate the Service Selection

        validateStatus() {
            const status = this.formFields.status;
            if (!status) {
                this.formErrors.$errors.status = [this.$t('integration.statusRequired')]; // Add custom error for status
            } else {
                delete this.formErrors.$errors.status;
            }
        },
        reloadPage() {
            window.location.reload();
        },

        getfields(selectedService) {
            if (selectedService && selectedService.fields) {
                this.dynamicFields = Object.values(selectedService.fields);
            } else {
                this.dynamicFields = [];
            }
        },

        async fetchOrganizationData(id) {
            try {
                const response = await request({ method: 'get', url: `/admin/integrations/${id}` });
                const data = response;
                this.formFields.name = data.name || '';
                const selectedService = this.serviceOptions.find(option => option.id === data.integration_service_id);

                if (selectedService) {
                    this.formFields.integration_service_id = selectedService.id;
                    this.getfields(selectedService);
                }
                this.formFields.client_id = data.client_id || '';
                this.formFields.client_secret = data.client_secret || '';
                this.formFields.tenant_id = data.tenant_id || '';
                this.formFields.is_active = (data.is_active) ? 1 : 0;
                this.formFields.selected_date = data.limit || null;
                this.formFields.microsoft_client_id = data.microsoft_client_id || '';
                this.formFields.microsoft_client_secret = data.microsoft_client_secret || '';
                this.formFields.microsoft_tenant_id = data.microsoft_tenant_id || '';
                this.formFields.code = data.code || '';
                this.formFields.redirect_uri = data.redirect_uri || '';
                this.formFields.grant_type = data.grant_type || '';
                this.formFields.username = data.username || '';
                this.formFields.password = data.password || '';
                this.formFields.instance_url = data.instance_url || '';
                this.formFields.domain_admin_email = data.domain_admin_email || '';
                this.formFields.domain_name = data.domain_name || '';
                this.existing_google_credentials_url = data.google_service_account_credentials_json || '';
                this.formFields.product_service = data.product_service;
                this.formFields.api_access_token = data.api_access_token || '';
                this.originalForm = JSON.parse(JSON.stringify(this.formFields));
            } catch (error) {
                console.error('Error fetching organization data:', error);
            }
        },


        async onSubmit() {
            let formData = new FormData();
            formData.append("name", this.formFields.name || "");
            formData.append("integration_service_id", this.formFields.integration_service_id);
            formData.append("is_active", this.formFields.is_active || "");
            formData.append("tenant_id", this.formFields.tenantId || "");
            formData.append("limit", this.formFields.selected_date || "");
            formData.append("microsoft_client_id", this.formFields.microsoft_client_id || "");
            formData.append("microsoft_client_secret", this.formFields.microsoft_client_secret || "");
            formData.append("microsoft_tenant_id", this.formFields.microsoft_tenant_id || "");
            formData.append("code", this.formFields.code || "");
            formData.append("redirect_uri", this.formFields.redirect_uri || "");
            formData.append("grant_type", this.formFields.grant_type || "");
            formData.append("client_id", this.formFields.client_id || "");
            formData.append("client_secret", this.formFields.client_secret || "");
            formData.append("username", this.formFields.username || "");
            formData.append("password", this.formFields.password || "");
            formData.append("product_service", this.formFields.product_service);
            formData.append("instance_url", this.formFields.instance_url || "");
            formData.append("domain_name", this.formFields.domain_name || "");
            formData.append("domain_admin_email", this.formFields.domain_admin_email || "");
            formData.append("api_access_token", this.formFields.api_access_token || "");

            if (this.formFields.google_service_account_credentials_json) {
                formData.append("google_service_account_credentials_json", this.formFields.google_service_account_credentials_json);
            } else {
                formData.append("google_service_account_credentials_json", this.existing_google_credentials_url || "");
            }


            try {
                const response = await request({
                    method: 'post',
                    url: `/admin/integrations-update/${this.organizationId}`,
                    data: formData,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.message && response.message.includes("successfully")) {
                    this.showToast();
                    setTimeout(() => { this.$router.push({ name: 'Integrations' }); }, 1000);
                } else {
                    throw new Error(response.data.message || 'Error updating data');
                }
            } catch (error) {
                if (error.request && error.request.status && error.request.status === 422) {
                    this.formErrors = new Error(JSON.parse(error.request.responseText).errors);
                    return false;
                } else {
                    this.notifyError();
                }
            }
        },



        showToast() {
            const h = this.$createElement;
            const vNodesMsg = h('p', { class: ['text-center', 'mb-0'] }, [
                this.$t('integration.dataSaved')
            ]);
            // Create the title
            const vNodesTitle = h(
                'div',
                { class: ['d-flex', 'flex-grow-1', 'align-items-baseline', 'mr-2'] },
                [h('strong', { class: 'mr-2' }, this.$t('integration.dataSavedTitle'))]
            );
            // Pass the VNodes as an array for message and title
            this.$bvToast.toast([vNodesMsg], {
                title: [vNodesTitle],
                solid: true,
                variant: 'success',
            });
        },

        onCancel() {
            this.$router.push({ name: 'Integrations' });
        },
    },
    watch: {
        formFields: {
            deep: true,
            handler() {
                this.initializeVisibility(); // Ensure visibility state is updated when new fields are added
                this.checkForChanges();
            }
        }
    },
    created() {
        const id = this.$route.params.id;
        this.organizationId = id;
        this.fetchServices().then(() => {
            this.fetchOrganizationData(id);
        });
        this.initializeVisibility();
    },
};
</script>

<style scoped>
.integration-form {
  padding: 20px;
}

.form-group {
    margin-bottom: 20px;
}

h2 {
    margin-bottom: 1.5rem;
    color: #2c3e50;
    font-size: 2rem;
}

.error-input {
    border-color: #dc3545 !important;
}
</style>
